import { useQuery, useMutation, useQueryClient, QueryKey } from 'react-query';
import toast from 'react-hot-toast';

interface UseApiOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  successMessage?: string;
  invalidateKeys?: QueryKey[];
}

/**
 * Hook para hacer queries con React Query
 */
export const useApiQuery = <T>(
  key: QueryKey,
  queryFn: () => Promise<T | null>,
  options?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number;
    staleTime?: number;
    cacheTime?: number;
    onSuccess?: (data: T) => void;
    onError?: (error: any) => void;
  }
) => {
  return useQuery(key, queryFn, {
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      // No reintentar en errores 401, 403, 404
      if (error?.response?.status && [401, 403, 404].includes(error.response.status)) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};

/**
 * Hook para hacer mutaciones con React Query
 */
export const useApiMutation = <TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: UseApiOptions
) => {
  const queryClient = useQueryClient();

  return useMutation(mutationFn, {
    onSuccess: (data) => {
      // Invalidar queries especificadas
      if (options?.invalidateKeys) {
        options.invalidateKeys.forEach(key => {
          queryClient.invalidateQueries(key);
        });
      }

      // Mostrar toast de éxito
      if (options?.showSuccessToast !== false) {
        const message = options?.successMessage || 'Operación realizada correctamente';
        toast.success(message);
      }

      // Ejecutar callback de éxito
      options?.onSuccess?.(data);
    },
    onError: (error: any) => {
      // Mostrar toast de error
      if (options?.showErrorToast !== false) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Error inesperado';
        toast.error(errorMessage);
      }

      // Ejecutar callback de error
      options?.onError?.(error);
    },
  });
};

/**
 * Hook para invalidar queries específicas
 */
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidate: (keys: QueryKey | QueryKey[]) => {
      if (Array.isArray(keys[0])) {
        // Múltiples keys
        (keys as QueryKey[]).forEach(key => {
          queryClient.invalidateQueries(key);
        });
      } else {
        // Single key
        queryClient.invalidateQueries(keys as QueryKey);
      }
    },
    invalidateAll: () => {
      queryClient.invalidateQueries();
    },
    remove: (key: QueryKey) => {
      queryClient.removeQueries(key);
    },
  };
};

/**
 * Hook para prefetch de datos
 */
export const usePrefetch = () => {
  const queryClient = useQueryClient();

  return {
    prefetch: <T>(key: QueryKey, queryFn: () => Promise<T>, staleTime?: number) => {
      queryClient.prefetchQuery(key, queryFn, {
        staleTime: staleTime || 5 * 60 * 1000, // 5 minutos por defecto
      });
    },
  };
};

/**
 * Hook para gestionar el estado de loading global
 */
export const useGlobalLoading = () => {
  const queryClient = useQueryClient();
  
  const isFetching = queryClient.isFetching();
  const isMutating = queryClient.isMutating();
  
  return {
    isLoading: isFetching > 0 || isMutating > 0,
    isFetching: isFetching > 0,
    isMutating: isMutating > 0,
  };
};

/**
 * Keys para queries - Organizadas por dominio
 */
export const QueryKeys = {
  // Autenticación
  auth: {
    profile: ['auth', 'profile'] as const,
  },
  
  // Reportes
  reports: {
    all: ['reports'] as const,
    list: (filters?: any) => ['reports', 'list', filters] as const,
    detail: (id: number) => ['reports', 'detail', id] as const,
    byMachine: (machineId: number) => ['reports', 'byMachine', machineId] as const,
    byUser: (userId: number) => ['reports', 'byUser', userId] as const,
    today: () => ['reports', 'today'] as const,
    latest: (limit?: number) => ['reports', 'latest', limit] as const,
  },
  
  // Máquinas
  machines: {
    all: ['machines'] as const,
    list: (params?: any) => ['machines', 'list', params] as const,
    detail: (id: number) => ['machines', 'detail', id] as const,
    active: () => ['machines', 'active'] as const,
    byType: (type: string) => ['machines', 'byType', type] as const,
    stats: (id: number, dateRange?: any) => ['machines', 'stats', id, dateRange] as const,
    users: (id: number) => ['machines', 'users', id] as const,
  },
  
  // Usuarios
  users: {
    all: ['users'] as const,
    list: (params?: any) => ['users', 'list', params] as const,
    detail: (id: number) => ['users', 'detail', id] as const,
    active: () => ['users', 'active'] as const,
    byCompany: (company: string) => ['users', 'byCompany', company] as const,
    permissions: () => ['users', 'permissions'] as const,
    stats: (id: number, dateRange?: any) => ['users', 'stats', id, dateRange] as const,
    machines: (id: number) => ['users', 'machines', id] as const,
  },
  
  // Dashboard
  dashboard: {
    stats: () => ['dashboard', 'stats'] as const,
    reportsByDate: (startDate: string, endDate: string) => ['dashboard', 'reportsByDate', startDate, endDate] as const,
    reportsByMachine: (dateRange?: any) => ['dashboard', 'reportsByMachine', dateRange] as const,
    fuelStats: (dateRange?: any) => ['dashboard', 'fuelStats', dateRange] as const,
    productivityStats: (dateRange?: any) => ['dashboard', 'productivityStats', dateRange] as const,
    machineStatusStats: () => ['dashboard', 'machineStatusStats'] as const,
    trends: () => ['dashboard', 'trends'] as const,
    topMachines: (limit?: number, period?: string) => ['dashboard', 'topMachines', limit, period] as const,
    alerts: () => ['dashboard', 'alerts'] as const,
  },
} as const;

export default {
  useApiQuery,
  useApiMutation,
  useInvalidateQueries,
  usePrefetch,
  useGlobalLoading,
  QueryKeys,
};